import cloneDeep from 'lodash/cloneDeep'
import sortBy from 'lodash/sortBy'
import * as segmentationService from '@/services/segmentation.js'
import { apolloProvider } from '@/apollo-provider.js'
import { MUTATION_UPDATE_SEGMENTS, QUERY_SEGMENTS_TAGS } from './graphql'
import Vue from 'vue'

const triggerWaitStart = ({ commit, state, dispatch }) => {
  if (state.segmentationLoaderCounter === 0) dispatch('wait/start', 'evaluate-segmentation', { root: true })

  commit('SET_OBJECT', { name: 'segmentationLoaderCounter', data: state.segmentationLoaderCounter + 1, state })
}

const triggerWaitEnd = ({ commit, state, dispatch }) => {
  if (state.segmentationLoaderCounter === 1) dispatch('wait/end', 'evaluate-segmentation', { root: true })

  commit('SET_OBJECT', { name: 'segmentationLoaderCounter', data: state.segmentationLoaderCounter - 1, state })
}

const calculateSegmentSizeAndRFVClusters = async ({ state, commit }) => {
  try {
    const clusterData = await segmentationService.evaluateSegmentWithRFVClusters({ data: state.currentSegmentToEvaluate })
    commit('SET_OBJECT', { name: 'segmentationRFVClusters', data: { ...clusterData, valid: true }, state })
    commit('SET_OBJECT', {
      name: 'segmentationSize',
      data: {
        clients_qty: clusterData.count,
        count: clusterData.count,
        unlimited_count: clusterData.unlimited_count,
      },
      state,
    })
  } catch (error) {
    commit('SET_OBJECT', { name: 'segmentationRFVClusters', data: { ...state.segmentationRFVClusters, valid: false }, state })
    throw error
  }
}

const calculateSegmentSize = async ({ state, commit }) => {
  try {
    let clientsQty = 0
    if (state.currentSegmentToEvaluate?.groups?.[0]?.criteria?.length) {
      const data = await segmentationService.evaluateSegment({ data: state.currentSegmentToEvaluate })
      clientsQty = data.clients_qty || 0
    }
    commit('SET_OBJECT', { name: 'segmentationSize', data: { clients_qty: clientsQty }, state })
  } catch (error) {
    commit('SET_OBJECT', { name: 'segmentationSize', data: { clients_qty: 0 }, state })
    throw error
  }
}

export default {
  async getSegmentationStructure({ state, commit }, modules) {
    const data = await segmentationService.getSegmentationStructure(modules)
    commit('SET_OBJECT', { name: 'segmentation', data, state })
  },
  async evaluateSegmentation({ state, commit, dispatch }, { segment, forceUpdate, skipRFVClusters }) {
    const hasRfvPermission = !Vue.getLoggedUser().only_mercapromo && Vue.permissions.havePagePermission('clientes/rfv')
    commit('SET_OBJECT', { name: 'segmentationClientsCoordinates', data: {}, state })
    commit('SET_OBJECT', { name: 'segmentationStatusCount', data: {}, state })
    commit('SET_OBJECT', { name: 'currentSegmentToEvaluate', data: segment, state })

    if (state.autoUpdateSegmentInsights || forceUpdate) {
      if (!skipRFVClusters && hasRfvPermission) {
        await calculateSegmentSizeAndRFVClusters({ state, commit })
      } else {
        await calculateSegmentSize({ state, commit })
      }
      commit('SET_OBJECT', { name: 'isSegmentInsightsStale', data: false, state })

      if (state.autoUpdateSegmentInsights) {
        commit('SET_OBJECT', { name: 'persistOriginalSize', data: true, state })
      }
    } else {
      commit('SET_OBJECT', { name: 'isSegmentInsightsStale', data: true, state })
    }
  },

  async evaluateSegmentationGeoClientsCoordinates({ state, commit }, { segment, maxNumberOfClients = 250000 }) {
    const data = await segmentationService.evaluateSegmentGeoClientsCoordinates({
      segment: { data: segment },
      max_number_of_clients: maxNumberOfClients,
    })
    commit('SET_OBJECT', { name: 'segmentationClientsCoordinates', data, state })
  },

  async evaluateSegmentationGeoStatusCount({ state, commit }, { segment, maxNumberOfClients = 250000 }) {
    const data = await segmentationService.evaluateSegmentGeoStatusCount({
      segment: { data: segment },
      max_number_of_clients: maxNumberOfClients,
    })
    commit('SET_OBJECT', { name: 'segmentationStatusCount', data, state })
  },
  setAutoUpdateSegmentInsights({ state, commit }, value) {
    commit('SET_OBJECT', { name: 'autoUpdateSegmentInsights', data: value, state })
  },
  setDraggedObject({ state, commit }, object) {
    commit('SET_OBJECT', { name: 'draggedObject', data: object, state })
  },
  setSelectedCriteria({ state, commit }, criteria) {
    commit('SET_OBJECT', { name: 'selectedCriteria', data: criteria, state })
  },

  setSegmentationGroups({ state, commit }, groups) {
    commit('SET_OBJECT', { name: 'segmentationGroups', data: groups, state })
  },

  setSegmentationTabIndex({ state, commit }, index) {
    commit('SET_OBJECT', { name: 'segmentationTabIndex', data: index, state })
  },

  selectCriteria({ state, commit }, { groupIndex, criteriaIndex, newCriteria }) {
    if (!state.selectedCriteria) {
      let criteria = {
        criteriaValue: state.segmentationGroups.groups[groupIndex].criteria[criteriaIndex],
        groupIndex,
        criteriaIndex,
        new: newCriteria,
      }
      commit('SET_OBJECT', { name: 'selectedCriteria', data: criteria, state })
      commit('SET_OBJECT', { name: 'persistOriginalSize', data: false, state })
    }
  },

  async deleteSegmentationCriteria({ state, commit, dispatch }, { groupIndex, criteriaIndex }) {
    // Deep clone current segmentation groups state to avoid contamination
    let segmentationGroups = cloneDeep(state.segmentationGroups)

    // Splice to remove the criteria
    segmentationGroups.groups[groupIndex].criteria.splice(criteriaIndex, 1)

    // If the group is left without criteria, delete it
    if (!segmentationGroups.groups[groupIndex].criteria.length) {
      segmentationGroups.groups.splice(groupIndex, 1)
    }
    commit('SET_OBJECT', { name: 'segmentationGroups', data: segmentationGroups, state })
    commit('SET_OBJECT', { name: 'persistOriginalSize', data: false, state })

    triggerWaitStart({ commit, state, dispatch })

    try {
      await dispatch('evaluateSegmentation', { segment: segmentationGroups })
    } catch (error) {
      console.error(error)
    } finally {
      triggerWaitEnd({ commit, state, dispatch })
    }
  },

  async deleteSegmentationGroup({ state, commit, dispatch }, groupIndex) {
    // Deep clone current segmentation groups state to avoid contamination
    let segmentationGroups = cloneDeep(state.segmentationGroups)

    // Splice to remove the group
    segmentationGroups.groups.splice(groupIndex, 1)

    commit('SET_OBJECT', { name: 'segmentationGroups', data: segmentationGroups, state })
    commit('SET_OBJECT', { name: 'persistOriginalSize', data: false, state })

    triggerWaitStart({ commit, state, dispatch })

    try {
      await dispatch('evaluateSegmentation', { segment: segmentationGroups })
    } catch (error) {
      console.error(error)
    } finally {
      triggerWaitEnd({ commit, state, dispatch })
    }
  },

  async updateSegmentationLimiter({ state, commit, dispatch }, limiter) {
    state.segmentationGroups.limiter = limiter
    commit('SET_OBJECT', { name: 'segmentationGroups', data: state.segmentationGroups, state })

    commit('SET_OBJECT', { name: 'persistOriginalSize', data: true, state })

    triggerWaitStart({ commit, state, dispatch })

    try {
      await dispatch('evaluateSegmentation', { segment: state.segmentationGroups })
    } catch (error) {
      console.error(error)
    } finally {
      triggerWaitEnd({ commit, state, dispatch })
    }
  },

  saveSelectedCriteria({ state, commit }) {
    // Deep clone current segmentation groups state to avoid contamination
    let segmentationGroups = cloneDeep(state.segmentationGroups)
    const selectedCriteria = state.selectedCriteria
    segmentationGroups.groups[selectedCriteria.groupIndex].criteria[selectedCriteria.criteriaIndex] = selectedCriteria.criteriaValue

    commit('SET_OBJECT', { name: 'segmentationGroups', data: segmentationGroups, state })
  },

  addCriteriaToLastPosition({ state, commit }, criteria) {
    let segmentationGroups = cloneDeep(state.segmentationGroups)
    const lastGroup = segmentationGroups.groups[segmentationGroups.groups.length - 1]
    const lastCriteriaIndex = lastGroup.length ? lastGroup.length - 1 : 0
    lastGroup.criteria[lastCriteriaIndex] = criteria

    commit('SET_OBJECT', { name: 'segmentationGroups', data: segmentationGroups, state })
    commit('SET_OBJECT', { name: 'persistOriginalSize', data: false, state })
  },

  async copyCriteriaToGroup({ state, commit, dispatch }, { groupIndex, criteriaIndex, newGroupIndex }) {
    const segmentationGroups = cloneDeep(state.segmentationGroups)
    const criteria = cloneDeep(state.segmentationGroups.groups[groupIndex].criteria[criteriaIndex])
    if (newGroupIndex !== null) {
      segmentationGroups.groups[newGroupIndex].criteria.push(criteria)
    } else {
      segmentationGroups.groups.push({
        criteria: [criteria],
        operator: 'OR',
      })
    }
    commit('SET_OBJECT', { name: 'segmentationGroups', data: segmentationGroups, state })

    triggerWaitStart({ commit, state, dispatch })

    try {
      await dispatch('evaluateSegmentation', { segment: segmentationGroups })
    } catch (error) {
      console.error(error)
    } finally {
      triggerWaitEnd({ commit, state, dispatch })
    }
  },

  async setGroupOperator({ state, commit, dispatch }, { groupIndex, operator }) {
    let segmentationGroups = cloneDeep(state.segmentationGroups)
    segmentationGroups.groups[groupIndex].operator = operator

    commit('SET_OBJECT', { name: 'segmentationGroups', data: segmentationGroups, state })
    commit('SET_OBJECT', { name: 'persistOriginalSize', data: false, state })

    triggerWaitStart({ commit, state, dispatch })

    try {
      await dispatch('evaluateSegmentation', { segment: segmentationGroups })
    } catch (error) {
      console.error(error)
    } finally {
      triggerWaitEnd({ commit, state, dispatch })
    }
  },

  async setSegmentationOperator({ state, commit, dispatch }, { operator }) {
    let segmentationGroups = cloneDeep(state.segmentationGroups)
    segmentationGroups.operator = operator

    commit('SET_OBJECT', { name: 'segmentationGroups', data: segmentationGroups, state })
    commit('SET_OBJECT', { name: 'persistOriginalSize', data: false, state })

    triggerWaitStart({ commit, state, dispatch })

    try {
      await dispatch('evaluateSegmentation', { segment: segmentationGroups })
    } catch (error) {
      console.error(error)
    } finally {
      triggerWaitEnd({ commit, state, dispatch })
    }
  },

  setSelectedCriteriaText({ state, commit }, { text, currentIndex }) {
    let selectedCriteria = cloneDeep(state.selectedCriteria)
    selectedCriteria.criteriaValue[currentIndex].text = text

    commit('SET_OBJECT', { name: 'selectedCriteria', data: selectedCriteria, state })
  },

  async setIsValidSegmentation({ state, commit, dispatch }, isValid) {
    commit('SET_OBJECT', { name: 'segmentationIsValid', data: isValid, state })

    if (isValid) {
      triggerWaitStart({ commit, state, dispatch })

      try {
        await dispatch('evaluateSegmentation', { segment: state.segmentationGroups })
      } catch (error) {
        console.error(error)
      } finally {
        triggerWaitEnd({ commit, state, dispatch })
      }
    }
  },

  async getSegments({ state, commit }) {
    const data = await segmentationService.getSegments()
    commit('SET_OBJECT', { name: 'segments', data, state })
  },

  async saveSegment({ state, commit }, segment) {
    const data = await segmentationService.saveSegment(segment)
    return data
  },

  async getSegmentById({ state, commit }, id) {
    const data = await segmentationService.getSegmentById(id)
    return data
  },

  async editSegment({ state, commit }, segment) {
    const data = await segmentationService.editSegment(segment)
    return data
  },

  async deleteSegment({ state, commit }, id) {
    const data = await segmentationService.deleteSegment(id)
    return data
  },

  async getSegmentModels({ state, commit }, filters) {
    const data = await segmentationService.getSegmentModels(filters)
    commit('SET_OBJECT', { name: 'segmentationModels', data, state })
  },

  async getSegmentModelById({ state, commit }, id) {
    const data = await segmentationService.getSegmentModelById(id)
    commit('SET_OBJECT', { name: 'segmentModel', data, state })
    return data
  },

  async exportSegmentClients(_, { filter, fileName }) {
    await segmentationService.getSegmentationClients(filter, fileName)
  },

  async exportSegmentProducts(_, { filter, fileName }) {
    await segmentationService.getSegmentationProducts(filter, fileName)
  },

  async exportSegmentPurchaseInfo(_, { filter, fileName }) {
    await segmentationService.getSegmentationPurchaseInfo(filter, fileName)
  },

  async cloneSegment({ state, commit }, segment) {
    const data = await segmentationService.cloneSegment(segment)
    return data
  },
  updateSegments({ commit }, segments_ids) {
    commit('SET_UPDATING_SEGMENTS_IDS', segments_ids)
    return new Promise((resolve, reject) => {
      apolloProvider.defaultClient
        .mutate({
          mutation: MUTATION_UPDATE_SEGMENTS,
          variables: {
            segments_ids: segments_ids,
          },
        })
        .then(({ data }) => {
          commit('SET_REFRESHED_SEGMENTS', data.refreshSegments)
          commit('SET_UPDATING_SEGMENTS_IDS', [])
        })
        .catch((error) => {
          commit('SET_UPDATING_SEGMENTS_IDS', [])
          reject(error)
        })
    })
  },
  setIsSegmentStale({ state, commit, dispatch }, isStale) {
    commit('SET_OBJECT', { name: 'isSegmentInsightsStale', data: isStale, state })
  },
  setPersistOriginalSize({ state, commit }, persistOriginalSize) {
    commit('SET_OBJECT', { name: 'persistOriginalSize', data: persistOriginalSize, state })
  },
  setLoadingSegmentInsights({ state, commit }, loading) {
    commit('SET_OBJECT', { name: 'loadingSegmentInsights', data: loading, state })
  },
  setSegmentationSize({ state, commit }, size) {
    commit('SET_OBJECT', { name: 'segmentationSize', data: size, state })
  },
  calculateSegmentSize,
  setTags({ commit }, tagList) {
    commit(
      'SET_TAGS',
      sortBy(tagList, (item) => item.name.toLowerCase())
    )
  },
  async fetchSegmentsTags({ commit, dispatch }) {
    try {
      const { data } = await apolloProvider.defaultClient.query({
        query: QUERY_SEGMENTS_TAGS,
        fetchPolicy: 'no-cache',
      })

      if (data && data.segments && data.segments.tags) {
        dispatch('setTags', data.segments.tags)
      }
    } catch (error) {
      console.error('Error fetching segments tags:', error)
    }
  },
  updateTag({ commit }, tag) {
    commit('UPDATED_TAG', tag)
  },
  deleteTag({ commit }, tag) {
    commit('DELETE_TAG', tag)
  },
  calculateSegmentSizeAndRFVClusters,
}
